import styled, { css } from 'styled-components';
import type { BackgroundValue } from '@elfsight/configurator-contracts';
import {
  isTransparentBackground,
  WidgetBackground as LibWidgetBackground
} from '@elfsight/widget-ui';
import type { FeedsSettings } from '../../types';
import type { DefaultComponentProps } from '../types';
import { useSocialFeedSettings } from '../utils';

const THUMBNAIL_PADDING = 40;
const THUMBNAIL_BORDER_RADIUS = 20;
const THUMBNAIL_BACKGROUND_COLOR = '#ffffff';
const ARROWS_CENTERING_THRESHOLD = 20;
const ARROW_CENTERING_MARGIN = -16;
const SHADOW_STYLE = { boxShadow: '0 6px 30px 0 rgba(0, 0, 0, 0.1)' };

type BackgroundSettingsGetterType = Pick<
  FeedsSettings,
  | 'widgetHorizontalPadding'
  | 'widgetVerticalPadding'
  | 'widgetBackground'
  | 'renderThumbnailMode'
>;

function getFinalBackgroundSettings({
  widgetHorizontalPadding,
  widgetVerticalPadding,
  widgetBackground,
  renderThumbnailMode
}: BackgroundSettingsGetterType): {
  shadowStyle: { boxShadow: string } | undefined;
  finalHorizontalPadding: number;
  finalVerticalPadding: number;
  finalWidgetBackground: BackgroundValue;
} {
  if (!renderThumbnailMode) {
    return {
      shadowStyle: undefined,
      finalHorizontalPadding: widgetHorizontalPadding,
      finalVerticalPadding: widgetVerticalPadding,
      finalWidgetBackground: widgetBackground
    };
  }

  if (isTransparentBackground(widgetBackground)) {
    return {
      shadowStyle: SHADOW_STYLE,
      finalHorizontalPadding: THUMBNAIL_PADDING,
      finalVerticalPadding: THUMBNAIL_PADDING,
      finalWidgetBackground: {
        type: 'color',
        color: THUMBNAIL_BACKGROUND_COLOR,
        borderRadius: THUMBNAIL_BORDER_RADIUS
      }
    };
  }

  return {
    shadowStyle: SHADOW_STYLE,
    finalHorizontalPadding: widgetHorizontalPadding,
    finalVerticalPadding: widgetVerticalPadding,
    finalWidgetBackground: {
      ...widgetBackground,
      borderRadius: widgetBackground.borderRadius || THUMBNAIL_BORDER_RADIUS
    }
  };
}

export function WidgetBackground({ children }: DefaultComponentProps) {
  const {
    widgetHorizontalPadding,
    widgetVerticalPadding,
    widgetBackground,
    renderThumbnailMode
  } = useSocialFeedSettings();

  const isTransparent = isTransparentBackground(widgetBackground);

  const {
    shadowStyle,
    finalHorizontalPadding,
    finalVerticalPadding,
    finalWidgetBackground
  } = getFinalBackgroundSettings({
    widgetHorizontalPadding,
    widgetVerticalPadding,
    widgetBackground,
    renderThumbnailMode
  });

  const areArrowsCentered =
    (renderThumbnailMode || !isTransparent) &&
    finalHorizontalPadding >= ARROWS_CENTERING_THRESHOLD;

  return (
    <StyledWidgetBackground
      {...finalWidgetBackground}
      _centerArrows={areArrowsCentered}
      horizontalPadding={finalHorizontalPadding}
      verticalPadding={finalVerticalPadding}
      style={shadowStyle}
    >
      {children}
    </StyledWidgetBackground>
  );
}

const StyledWidgetBackground = styled(LibWidgetBackground)<{
  _centerArrows: boolean;
}>`
  ${({ _centerArrows }) =>
    _centerArrows &&
    css`
      &
        [class*='Carousel__CarouselArrowControlContainer-sc'][aria-label='Previous'] {
        margin: 0 !important;
        left: ${ARROW_CENTERING_MARGIN}px;
      }

      &
        [class*='Carousel__CarouselArrowControlContainer-sc'][aria-label='Next'] {
        margin: 0 !important;
        right: ${ARROW_CENTERING_MARGIN}px;
      }
    `}
`;
